/**
 * Pressão unificada 0–1 para telemetry, retry, batch e sampling.
 */

import { getQueuePressureLevel } from '../backpressure';
import { computeMemoryPressure } from '../performance/memoryPressure';
import { getQueueMutable, getMaxQueueItemsLimit } from '../queue/queueState';
import { getPartitionPressure } from '../queue/partitionPolicies';
import { getQueuePartition } from '../queue/partitioning';

let replayCursorLagHint = 0;
let browserLagHint = 0;
let storageFallbackHint = 0;
let storageUnhealthyHint = false;
let retryStormPerMinHint = 0;

export function setReplayCursorLagHint(lag: number): void {
  replayCursorLagHint = lag;
}

export function setStoragePressureHints(fallbackCount: number, healthOk: boolean): void {
  storageFallbackHint = fallbackCount;
  storageUnhealthyHint = !healthOk;
}

export function setRetryStormPerMinHint(perMin: number): void {
  retryStormPerMinHint = perMin;
}

export function setBrowserLagHint(lag: number): void {
  browserLagHint = Math.min(1, lag);
}

export function getBrowserLagPressure(): number {
  return browserLagHint;
}

export function getPartitionPressureLevel(): number {
  const queue = getQueueMutable();
  if (queue.length === 0) return 0;
  const parts = new Set(queue.map(getQueuePartition));
  let max = 0;
  for (const p of parts) {
    max = Math.max(max, getPartitionPressure(p, queue));
  }
  return max;
}

export function getReplayPressureLevel(): number {
  return Math.min(1, replayCursorLagHint / 50);
}

export function getStoragePressureLevel(): number {
  let p = 0;
  if (storageUnhealthyHint) p += 0.5;
  if (storageFallbackHint > 0) p += Math.min(0.3, storageFallbackHint * 0.1);
  return Math.min(1, p);
}

export function getRetryStormPressure(): number {
  return Math.min(1, retryStormPerMinHint / 120);
}

export function getUnifiedPressureLevel(): number {
  const backpressure = getQueuePressureLevel() / 3;
  const memory = computeMemoryPressure(
    getQueueMutable(),
    getMaxQueueItemsLimit()
  ).level;
  return Math.min(
    1,
    Math.max(
      backpressure,
      memory,
      getReplayPressureLevel(),
      getStoragePressureLevel(),
      getRetryStormPressure(),
      getPartitionPressureLevel(),
      getBrowserLagPressure()
    )
  );
}

export function getPressureBreakdown(): Readonly<{
  queue: number;
  memory: number;
  replay: number;
  storage: number;
  retryStorm: number;
  partition: number;
  browserLag: number;
  unified: number;
}> {
  const queue = getQueuePressureLevel() / 3;
  const memory = computeMemoryPressure(
    getQueueMutable(),
    getMaxQueueItemsLimit()
  ).level;
  const replay = getReplayPressureLevel();
  const storage = getStoragePressureLevel();
  const retryStorm = getRetryStormPressure();
  const partition = getPartitionPressureLevel();
  const browserLag = getBrowserLagPressure();
  return {
    queue,
    memory,
    replay,
    storage,
    retryStorm,
    partition,
    browserLag,
    unified: Math.min(
      1,
      Math.max(queue, memory, replay, storage, retryStorm, partition, browserLag)
    ),
  };
}
